import { useState } from 'react';
import { OverviewBody } from 'modules/arena/reports/model/bodies';
import { formatPeriod } from 'modules/arena/reports/model/dates';
import { Metric } from 'modules/arena/reports/model/types';
import { KpiGrid } from 'modules/arena/reports/components/KpiCard';
import { Panel } from 'modules/arena/reports/components/Panel';
import { MoneyBasisToggle } from 'modules/arena/reports/components/MoneyBasisToggle';
import { DataTable } from 'modules/arena/reports/components/DataTable';
import { AreaProps, barsToReais, BASIS_TITLE, formatReais, GROUPING_LABEL, toReais } from 'modules/arena/reports/areas/shared';
import { RevenueTrendChart } from 'shared/components/charts/RevenueTrendChart';
import { MagnitudeBarList } from 'shared/components/charts/MagnitudeBarList';
import { PillTabs } from 'shared/components/ui/PillTabs';

type Breakdown = 'sport' | 'unit' | 'product';
const BREAKDOWNS: { value: Breakdown; label: string }[] = [
  { value: 'sport', label: 'Esportes' },
  { value: 'unit', label: 'Unidades' },
  { value: 'product', label: 'Produtos' },
];

export function OverviewArea({ body, meta, controller }: AreaProps<OverviewBody>) {
  const { moneyBasis, setMoneyBasis, applied, selectArea } = controller;
  const [breakdown, setBreakdown] = useState<Breakdown>('sport');
  const { series, breakdowns } = body;
  const title = `${BASIS_TITLE[moneyBasis]} ${GROUPING_LABEL[applied.common.grouping]}`;

  const open = (metric: Metric) => metric.drillDown && selectArea(metric.drillDown.area, metric.drillDown.specific);

  return (
    <div className="flex flex-col gap-6">
      <KpiGrid metrics={body.metrics} meta={meta} onOpen={open} />

      {/* Sem acesso ao financeiro a fonte não envia série nem quebras em dinheiro. */}
      {series && breakdowns && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.5fr_1fr]">
          <Panel
            title={title}
            subtitle={`Pela data do recebimento · ${formatPeriod(meta.period.start, meta.period.end)}`}
            actions={<MoneyBasisToggle value={moneyBasis} onChange={setMoneyBasis} />}
          >
            <RevenueTrendChart
              points={toReais(series[moneyBasis])}
              comparePoints={series.previous ? toReais(series.previous[moneyBasis]) : undefined}
              compareLabel={meta.previousPeriod ? `Período anterior (${formatPeriod(meta.previousPeriod.start, meta.previousPeriod.end)})` : undefined}
              formatValue={formatReais}
              ariaLabel={title}
            />
          </Panel>

          <Panel
            title={`${BASIS_TITLE[moneyBasis]} por ${BREAKDOWNS.find((b) => b.value === breakdown)!.label.toLowerCase()}`}
            subtitle="Pela data do recebimento, no período aplicado"
            actions={<PillTabs options={BREAKDOWNS} value={breakdown} onChange={setBreakdown} />}
          >
            {breakdowns[breakdown][moneyBasis].length ? (
              <MagnitudeBarList items={barsToReais(breakdowns[breakdown][moneyBasis])} formatValue={formatReais} />
            ) : (
              <p className="text-sm text-ink-secondary">Nenhum recebimento no período.</p>
            )}
          </Panel>
        </div>
      )}

      <DataTable
        title="Resumo por unidade"
        table={body.table}
        footnote={
          series
            ? 'Totais calculados sobre todo o conjunto filtrado. Valor vendido usa a data da venda; recebido e líquido, a data do recebimento.'
            : 'Totais calculados sobre todo o conjunto filtrado.'
        }
      />
    </div>
  );
}
